import React, { useState, useEffect, useRef } from "react";
import DepositorForm from "./DepositorForm";
import DepositorHistory from "./DepositorHistory";
import { useAuth } from "../../contexts/AuthContext";
import "./DepositorDashboard.css";
import { FaHome, FaHistory, FaSignOutAlt, FaCheckCircle, FaBars, FaTimes, FaQuestionCircle } from "react-icons/fa";
import Profile from "./Profile";
import HelpPanel from "./HelpPanel";

export default function DepositorDashboard() {
  const { user, logout } = useAuth();

  const [activeTab, setActiveTab] = useState("home");
  const [showThankYouModal, setShowThankYouModal] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [draftCount, setDraftCount] = useState(0);
  // Holds the specific draft entry to load into the Form, set the moment
  // "Edit" is clicked in History — passed down as a prop instead of
  // relying on a localStorage flag + lazy initializer timing, which is
  // unreliable under React 18 StrictMode's double-mount behavior in dev.
  const [entryToEdit, setEntryToEdit] = useState(null);

  // Keep the Home page's draft count in sync with the shared entries list.
  useEffect(() => {
    const readCount = () => {
      try {
        const all = JSON.parse(localStorage.getItem("dshb_entries") || "[]");
        setDraftCount(Array.isArray(all) ? all.filter(e => e.status === "draft").length : 0);
      } catch { setDraftCount(0); }
    };
    readCount();
    window.addEventListener("focus", readCount);
    return () => window.removeEventListener("focus", readCount);
  }, [activeTab]);

  // ── Welcome tour ──────────────────────────────────────────────────
  // Lives here, not inside DepositorForm, because several steps point at
  // things outside the form entirely (History, Profile, Help in the
  // navbar, which always render regardless of which tab is active).
  // tourRefs is a shared bucket — DepositorForm registers its own
  // elements into it via callback refs, keyed by string, so this
  // component can read them without passing individual refs down.
  const tourRefs = useRef({});
  const TOUR_SEEN_KEY = "dshb_draft_tour_seen";
  const [tourActive, setTourActive] = useState(false);
  const [tourIndex, setTourIndex] = useState(0);
  const [tourPos, setTourPos] = useState(null);

  const TOUR_STEPS = [
    { refKey: "downloadTemplate", tab: "form",
      title: "Have several antibodies to add?",
      body: "Click here to download a CSV template with instructions on how to fill it in. Fill it out, then come back and upload it." },
    { refKey: "uploadTemplate", tab: "form",
      title: "Upload your filled-in template",
      body: "Once your CSV template has your antibodies added, upload it here — each row becomes a draft entry below, ready to review." },
    { refKey: "exportCsv", tab: "form",
      title: "Export your drafts",
      body: "Downloads everything you've entered and saved as a draft, as a CSV file — handy for your own records." },
    { refKey: "submitAll", tab: "form",
      title: "Submit All",
      body: "Turns on once every entry has all its required info filled in. Greyed out? One or more entries is still missing something — check the list below it." },
    { refKey: "draftEntries", tab: "form",
      title: "Your draft entries",
      body: "Everything you add — by form or by CSV upload — is saved here automatically. Edit or delete anytime. If something needs fixing, the row shows \"Edit to Complete\" and exactly what's missing." },
    { refKey: "itemName", tab: "form",
      title: "Item Name / Number",
      body: "Your antibody's name. If it matches one already in our records, we'll let you know and show you how to make it unique." },
    { refKey: "institution", tab: "form",
      title: "Depositor Institution",
      body: "Start typing your university's name and pick it from the list. Can't find yours? Click \"Enter it manually\" below the field to type it in directly instead." },
    { refKey: "positiveSpecies", tab: "form",
      title: "Positive Tested Species",
      body: "List the species you've confirmed reactivity in. Below, check the confirmation box for each one — that's required. Attaching a document, image, or article as proof is optional, via Add Proof." },
    { refKey: "uniprot", tab: "form",
      title: "UniProt ID",
      body: "Optional — enter one if you have it. If it doesn't match a real entry, we'll flag it right away with a small message." },
    { refKey: "history", tab: null,
      title: "Find your past submissions",
      body: "History holds everything — drafts you've started and antibodies you've already submitted to DSHB." },
    { refKey: "profile", tab: null,
      title: "Your profile",
      body: "Update your name, email, or other account details here anytime." },
    { refKey: "help", tab: null,
      title: "Need more instructions?",
      body: "Open Help anytime for the full written guide — you can replay this tour from there too." },
    { refKey: null, tab: null,
      title: "Still have questions?",
      body: (
        <>
          Reach out to{" "}
          <a href="mailto:dshb-deposits@uiowa.edu" style={{ color: "#305DBD", fontWeight: 700, textDecoration: "underline" }}>
            dshb-deposits@uiowa.edu
          </a>{" "}
          anytime — we're happy to help.
        </>
      ) },
  ];

  const startTour = () => {
    setHelpOpen(false);
    setMobileMenuOpen(false);
    setTourIndex(0);
    setTourPos(null);
    setTourActive(true);
  };
  const closeTour = () => {
    setTourActive(false);
    try { localStorage.setItem(TOUR_SEEN_KEY, "1"); } catch { /* ignore */ }
  };
  const tourNext = () => {
    setTourIndex(i => {
      if (i >= TOUR_STEPS.length - 1) { closeTour(); return i; }
      return i + 1;
    });
  };
  const tourPrev = () => setTourIndex(i => Math.max(0, i - 1));

  // Auto-run once per browser, the first time the form tab is opened —
  // most of the tour is about the form, and it doesn't exist before then.
  useEffect(() => {
    if (activeTab !== "form") return;
    try {
      if (!localStorage.getItem(TOUR_SEEN_KEY)) {
        localStorage.setItem(TOUR_SEEN_KEY, "1");
        const t = setTimeout(startTour, 500);
        return () => clearTimeout(t);
      }
    } catch { /* ignore */ }
  }, [activeTab]);

  // Keeps the spotlight + tooltip glued to the real element. Switches tabs
  // first if the step's target lives on a different tab (the effect then
  // re-runs once activeTab updates), then polls briefly for the ref to
  // mount — DepositorForm doesn't exist in the DOM yet the instant
  // setActiveTab("form") is called, only after its own render commits.
  useEffect(() => {
    if (!tourActive) return;
    const step = TOUR_STEPS[tourIndex];
    if (!step) return;

    if (step.tab && activeTab !== step.tab) { setActiveTab(step.tab); return; }

    if (!step.refKey) { setTourPos("center"); return; }

    let cancelled = false, pollId, tries = 0, scrollFn, resizeFn;
    const attach = (el) => {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      const recompute = () => {
        const r = el.getBoundingClientRect();
        setTourPos({ top: r.top, left: r.left, width: r.width, height: r.height });
      };
      scrollFn = recompute; resizeFn = recompute;
      pollId = setTimeout(recompute, 320);
      window.addEventListener("scroll", scrollFn, true);
      window.addEventListener("resize", resizeFn);
    };
    const poll = () => {
      if (cancelled) return;
      const el = tourRefs.current[step.refKey];
      if (el) attach(el);
      else if (tries++ < 30) pollId = setTimeout(poll, 100);
      else tourNext(); // target never appeared — don't hang the tour forever
    };
    poll();

    return () => {
      cancelled = true;
      clearTimeout(pollId);
      if (scrollFn) window.removeEventListener("scroll", scrollFn, true);
      if (resizeFn) window.removeEventListener("resize", resizeFn);
    };
  }, [tourActive, tourIndex, activeTab]);

  // Only the currently-VISIBLE instance of a nav item should register —
  // desktop and mobile both render navItems, one of them hidden via CSS
  // at any given width, and a hidden element's rect is meaningless.
  const setNavTourRef = (key) => (el) => {
    if (el && el.offsetParent !== null) tourRefs.current[key] = el;
  };

  const handleSubmission = () => {
    setShowThankYouModal(true);
    setTimeout(() => {
      setShowThankYouModal(false);
      setActiveTab("history");
    }, 3000);
  };

  // Called by DepositorHistory's "Edit" button on a draft, with the full
  // entry object already in hand (no localStorage round-trip needed —
  // History already has the entry, just pass it straight through).
  const handleEditEntry = (entry) => {
    setEntryToEdit(entry);
    setActiveTab("form");
    setMobileMenuOpen(false);
  };

  // Initials for the identity badge — e.g. "Kavya I" -> "KI"
  const initials = `${user.firstName?.[0] || ""}${user.lastName?.[0] || ""}`.toUpperCase();

  const navItems = (
    <>
      <div className="nav-primary">
        <div
          className={`nav-link ${activeTab === "home" ? "active" : ""}`}
          onClick={() => { setActiveTab("home"); setMobileMenuOpen(false); }}
        >
          <FaHome /> Home
        </div>
        <div
          ref={setNavTourRef("history")}
          className={`nav-link ${activeTab === "history" ? "active" : ""}`}
          onClick={() => { setActiveTab("history"); setMobileMenuOpen(false); }}
        >
          <FaHistory /> History
        </div>
      </div>

      <div className="nav-account">
        <div
          ref={setNavTourRef("profile")}
          className={`nav-identity ${activeTab === "profile" ? "active" : ""}`}
          onClick={() => { setActiveTab("profile"); setMobileMenuOpen(false); }}
          title="View profile"
        >
          <span className="nav-identity-avatar">{initials}</span>
          <span className="nav-identity-name">{user.firstName} {user.lastName}</span>
        </div>
        <button
          ref={setNavTourRef("help")}
          className="nav-icon-btn"
          onClick={() => { setHelpOpen(true); setMobileMenuOpen(false); }}
          title="Help"
        >
          <FaQuestionCircle />
        </button>
        <button className="nav-logout-btn" onClick={logout}>
          <FaSignOutAlt /> <span className="nav-logout-label">Logout</span>
        </button>
      </div>
    </>
  );

  return (
    <>
      <header className="dashboard-navbar">
        <div className="navbar-left">
          <div className="navbar-logo">
            <img src="/dshb-logo.png" alt="DSHB Logo" className="logo-img" />
          </div>
        </div>

        {/* Desktop nav — hidden on small screens via CSS */}
        <div className="navbar-right navbar-right--desktop">
          {navItems}
        </div>

        {/* Hamburger toggle — shown only on small screens via CSS */}
        <button
          className="hamburger-btn"
          onClick={() => setMobileMenuOpen((o) => !o)}
          aria-label="Toggle menu"
        >
          {mobileMenuOpen ? <FaTimes /> : <FaBars />}
        </button>
      </header>

      {/* Mobile slide-down menu */}
      {mobileMenuOpen && (
        <div className="mobile-nav-menu">
          {navItems}
        </div>
      )}

      {/* Thank You Modal */}
      {showThankYouModal && (
        <div className="thank-you-overlay">
          <div className="thank-you-modal">
            <div className="thank-you-icon">
              <FaCheckCircle />
            </div>
            <h2>Thank You!</h2>
            <p className="thank-you-title">Submission Successful</p>
            <p className="thank-you-message">
              We appreciate your contribution to the scientific community. Your antibody
              details have been received and are now under review.
            </p>
            <div className="thank-you-content">
              <p><strong>What happens next?</strong></p>
              <ul>
                <li>Our team will review your submission</li>
                <li>We'll validate the antibody information</li>
                <li>If we have any questions or need additional information, we'll reach out to you</li>
                <li>Once approved, your antibody will be available to the global research community</li>
              </ul>
            </div>
            <p className="thank-you-footer">
              If you have any doubts or need information, please don't hesitate to contact us.
            </p>
            <p className="thank-you-signature">
              Thank you for supporting the advancement of scientific research!
            </p>
          </div>
        </div>
      )}

      <div className="dashboard-container">
        <section className="dashboard-info">

          {activeTab === "home" && (
            <div className="home-content">
              {draftCount > 0 && (
                <div style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  flexWrap: "wrap", gap: "12px",
                  padding: "14px 18px", marginBottom: "20px",
                  background: "#fefce8", border: "1px solid #fde68a", borderRadius: "8px",
                }}>
                  <span style={{ fontSize: "13px", color: "#854d0e" }}>
                    You have <strong>{draftCount} draft {draftCount === 1 ? "entry" : "entries"}</strong> waiting to be submitted.
                  </span>
                  <button
                    onClick={() => setActiveTab("form")}
                    style={{
                      background: "#1a2c5b", color: "#fff", border: "none", borderRadius: "6px",
                      padding: "7px 16px", cursor: "pointer", fontSize: "12px", fontWeight: 600,
                    }}
                  >
                    Continue Drafts
                  </button>
                </div>
              )}
              <div style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                flexWrap: "wrap", gap: "12px",
                padding: "14px 18px", marginBottom: "20px",
                background: "#E1E9FC", border: "1px solid #c7d6fb", borderRadius: "8px",
              }}>
                <span style={{ fontSize: "13px", color: "#14161B" }}>
                  <strong>New to this website?</strong> Take a 2-minute guided tour — we'll show you around.
                </span>
                <button
                  onClick={startTour}
                  style={{
                    background: "#305DBD", color: "#fff", border: "none", borderRadius: "6px",
                    padding: "7px 16px", cursor: "pointer", fontSize: "12px", fontWeight: 600,
                  }}
                >
                  ▶ Start the tour
                </button>
              </div>
              <h2>Welcome to DSHB Depositor Portal</h2>
              <p>
                The <strong>Developmental Studies Hybridoma Bank (DSHB)</strong> is a
                nonprofit repository at the University of Iowa dedicated to making
                high-quality monoclonal antibodies and hybridoma cell lines freely available
                to the research community.
              </p>
              <p>
                Depositing your antibody with DSHB means other labs can access, validate,
                and build on your work — accelerating discovery in developmental biology,
                neuroscience, cell biology, and beyond.
              </p>
              <p>
                DSHB-distributed antibodies are used worldwide for{" "}
                <em>immunostaining, western blotting, flow cytometry, ELISA,</em> and more.
                Your deposit becomes a lasting resource for scientists across the globe.
              </p>
              <div className="deposit-btn-wrapper">
                <button className="deposit-btn" onClick={() => setActiveTab("form")}>
                  Deposit Antibody
                </button>
              </div>
            </div>
          )}

          {activeTab === "form" && (
            <DepositorForm
              onSubmitAll={handleSubmission}
              entryToEdit={entryToEdit}
              onEntryToEditConsumed={() => setEntryToEdit(null)}
              tourRefs={tourRefs}
              onStartTour={startTour}
            />
          )}

          {activeTab === "history" && (
            // ← Pass user_id (the DB primary key) — not email.
            // onEditEntry lets History switch back to the Form tab
            // when the user clicks "Edit" on a draft.
            <DepositorHistory
              userId={user?.user_id}
              onEditEntry={handleEditEntry}
            />
          )}

          {activeTab === "profile" && (
            <Profile onBack={() => setActiveTab("home")} />
          )}

        </section>
      </div>

      <HelpPanel open={helpOpen} onClose={() => setHelpOpen(false)} onStartTour={startTour} />

      {/* Welcome tour — a ring drawn around the REAL element (from its
          live getBoundingClientRect) plus a tooltip next to it. Fixed
          positioning, rendered at this top level so it survives tab
          switches mid-tour. */}
      {tourActive && tourPos && (
        <>
          <div style={{ position: "fixed", inset: 0, background: "rgba(17,24,39,0.35)", zIndex: 1998 }} onClick={closeTour} />
          {tourPos !== "center" && (
            <div style={{
              position: "fixed",
              top: tourPos.top - 5, left: tourPos.left - 5,
              width: tourPos.width + 10, height: tourPos.height + 10,
              border: "2px solid #2563eb", borderRadius: "8px",
              boxShadow: "0 0 0 4px rgba(37,99,235,0.25)",
              zIndex: 1999, pointerEvents: "none",
            }} />
          )}
          <div style={tourPos === "center" ? {
            position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)",
            width: "340px", background: "#fff", borderRadius: "10px",
            boxShadow: "0 12px 32px rgba(0,0,0,0.28)", zIndex: 2000, padding: "18px 20px",
          } : {
            position: "fixed",
            top: Math.min(tourPos.top + tourPos.height + 12, window.innerHeight - 230),
            left: Math.min(Math.max(tourPos.left, 16), window.innerWidth - 356),
            width: "340px", background: "#fff", borderRadius: "10px",
            boxShadow: "0 12px 32px rgba(0,0,0,0.28)", zIndex: 2000, padding: "16px 18px",
          }}>
            <div style={{ fontSize: "10.5px", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: ".04em", marginBottom: "4px" }}>
              Step {tourIndex + 1} of {TOUR_STEPS.length}
            </div>
            <h3 style={{ margin: "0 0 6px", fontSize: "14px", color: "#111827" }}>{TOUR_STEPS[tourIndex]?.title}</h3>
            <p style={{ margin: "0 0 14px", fontSize: "12.5px", color: "#6b7280", lineHeight: 1.5 }}>{TOUR_STEPS[tourIndex]?.body}</p>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <button onClick={closeTour} style={{ background: "none", border: "none", color: "#9ca3af", fontSize: "11.5px", fontWeight: 600, textDecoration: "underline", cursor: "pointer", padding: 0 }}>Skip tour</button>
              <div style={{ display: "flex", gap: "8px" }}>
                {tourIndex > 0 && (
                  <button onClick={tourPrev} style={{ padding: "5px 12px", borderRadius: "5px", border: "1px solid #d1d5db", background: "#fff", fontSize: "12px", fontWeight: 600, cursor: "pointer", color: "#374151" }}>Back</button>
                )}
                <button onClick={tourNext} style={{ padding: "5px 14px", borderRadius: "5px", border: "none", background: "#1a2c5b", color: "#fff", fontSize: "12px", fontWeight: 600, cursor: "pointer" }}>
                  {tourIndex === TOUR_STEPS.length - 1 ? "Got it" : "Next"}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}